import { formatPoints } from "@iec-id/shared";
import { cn, Icon, Sheet, SquareBtn, squareBtnVariants } from "@iec-id/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/trpc";
import { useUiStore } from "@/stores/ui-store";
import { AccountButton } from "./account-button";
import { MobileNav } from "./mobile-nav";

export type AppHeaderProps = {
	onSignOut: () => void;
	navMeta?: Partial<Record<string, ReactNode>>;
};

/**
 * Шапка витрины: 64px белая карточка на десктопе и 56px стеклянная полоса
 * на мобильном (макеты 2a и 2b).
 *
 * Стекло — не декор: мобильная шапка липкая, под ней проезжает контент, и
 * плотная заливка на канве #E0E3E9 выглядела бы вторым фоном.
 *
 * Логотип — у левого края карточки шапки, правые контролы — у правого.
 *
 * ПОИСКА ЗДЕСЬ НЕТ (решение владельца 22.08.2026): разделов витрины семь, все
 * они видны в меню, и строка поиска занимала центр шапки ради навигации,
 * которая и так вся на виду. На мобильном она к тому же отнимала место у
 * баланса и колокольчика.
 */
export function AppHeader({ onSignOut, navMeta }: AppHeaderProps) {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const navOpen = useUiStore((state) => state.mobileNavOpen);
	const setNavOpen = useUiStore((state) => state.setMobileNavOpen);

	const unread = api.notifications.unreadCount.useQuery();
	const unreadCount = unread.data?.unread ?? 0;
	const bellLabel =
		unreadCount > 0
			? t("header.notificationsWithCount", { count: unreadCount })
			: t("header.notifications");

	return (
		<>
			{/* `shadow-header` стоит ПОСТОЯННО, а не включается по скроллу. Шапка
			    липкая и белая, карточки под ней тоже белые: без отбивки на скролле
			    они слипаются в одно полотно. Тень при этом подобрана так, что на
			    канве #E0E3E9 её не видно (тёмное на тёмном), а поверх белого она
			    проявляется сама — «появилась при скролле» получается физикой, без
			    слушателя события и без порога, на котором тень моргает. */}
			<header className="relative hidden h-16 items-center rounded-card bg-card py-2 shadow-header lg:flex">
				<Link
					to="/"
					className="iec-press-glyph absolute top-1/2 left-2 z-10 flex -translate-y-1/2 items-center gap-3 px-2 text-fg no-underline hover:text-fg"
				>
					<img src="/mark-globe.svg" alt="" width={48} height={48} className="block" />
					<span className="t-wordmark">{t("app.name")}</span>
				</Link>
				<div className="absolute top-1/2 right-2 z-10 flex -translate-y-1/2 items-center gap-2">
					<HeaderPoints />
					<Link
						to="/faq"
						aria-label={t("header.help")}
						className={cn(
							squareBtnVariants({ onGrey: true }),
							"cursor-pointer hover:bg-sunken-hover",
						)}
					>
						<Icon name="CircleHelp" />
					</Link>
					<SquareBtn
						onGrey
						aria-label={bellLabel}
						icon={<Icon name="Bell" />}
						{...(unreadCount > 0 ? { notification: unreadCount } : {})}
						className="cursor-pointer"
						onClick={() => void navigate({ to: "/notifications" })}
					/>
					<AccountButton onSignOut={onSignOut} />
				</div>
			</header>

			{/* Липкость — на обёртке в `app-shell.tsx`, а не здесь: внутри
			    родителя своей же высоты `sticky` не даёт ни пикселя хода.
			    Стекло остаётся: под шапкой проезжает содержимое, и плотная
			    заливка на канве читалась бы вторым фоном. */}
			<header className="flex h-14 items-center gap-1.5 bg-glass px-3 shadow-header backdrop-blur-[150px] lg:hidden">
				<SquareBtn
					size={40}
					onGrey
					aria-label={t("header.menu")}
					aria-expanded={navOpen}
					icon={<Icon name="Menu" />}
					className="cursor-pointer"
					onClick={() => setNavOpen(true)}
				/>
				<Link
					to="/"
					className="iec-press-glyph flex min-w-0 flex-grow items-center gap-2 text-fg no-underline hover:text-fg"
				>
					<img src="/mark-globe.svg" alt="" width={32} height={32} className="block shrink-0" />
					<span className="t-wordmark truncate">{t("app.name")}</span>
				</Link>
				<div className="flex shrink-0 items-center gap-1.5">
					<HeaderPoints compact />
					<SquareBtn
						size={40}
						onGrey
						aria-label={bellLabel}
						icon={<Icon name="Bell" />}
						{...(unreadCount > 0 ? { notification: unreadCount } : {})}
						className="cursor-pointer"
						onClick={() => void navigate({ to: "/notifications" })}
					/>
					<AccountButton variant="compact" onSignOut={onSignOut} />
				</div>
			</header>

			<Sheet
				side="left"
				open={navOpen}
				onOpenChange={setNavOpen}
				title={t("nav.label")}
				titleHidden
			>
				<MobileNav
					meta={navMeta}
					unreadCount={unreadCount}
					onSignOut={onSignOut}
					onNavigate={() => setNavOpen(false)}
				/>
			</Sheet>
		</>
	);
}

function HeaderPoints({ compact = false }: { compact?: boolean }) {
	const { t, i18n } = useTranslation("common");
	const navigate = useNavigate();
	const points = api.points.summary.useQuery();
	const classes = cn(
		"iec-press flex cursor-pointer items-center gap-1.5 rounded-control bg-sunken text-fg",
		"hover:bg-sunken-hover",
		"focus-ring",
		compact ? "h-10 px-2" : "h-12 px-2.5",
	);

	// Ошибка сводки не прячет плашку (§8: четыре состояния запроса): компактно
	// «—» с объяснением в title, клик — повтор запроса, а не переход в раздел.
	if (points.isError) {
		const errorLabel = t("header.pointsError");
		return (
			<button
				type="button"
				aria-label={errorLabel}
				title={errorLabel}
				onClick={() => void points.refetch()}
				className={cn(classes, "text-fg-muted")}
			>
				<Icon name="Coins" className="text-coin" />
				<span className="t-num-xs">—</span>
			</button>
		);
	}

	if (!points.data) return null;

	const label = t("header.points", { count: points.data.balance });

	return (
		<button
			type="button"
			aria-label={label}
			onClick={() => void navigate({ to: "/points" })}
			className={classes}
		>
			<Icon name="Coins" className="text-coin" />
			<span className="t-num-xs">{formatPoints(points.data.balance, i18n.language)}</span>
		</button>
	);
}
